import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import {
  CUSTOM_RATE_CODE,
  DIVIDEND_TAX_RATES,
  type DividendTaxRate,
  type Region,
} from "@/content/dividend-tax-rates";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

const REGION_ORDER: Region[] = ["europe", "americas", "asiaPacific", "mea", "other"];

interface Props {
  countryCode: string;
  customTaxRatePct: number;
  onCountryChange: (code: string) => void;
  onCustomRateChange: (pct: number) => void;
}

const CountryTaxPicker = ({
  countryCode,
  customTaxRatePct,
  onCountryChange,
  onCustomRateChange,
}: Props) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const regionNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.language], { type: "region" });
    } catch {
      return new Intl.DisplayNames(["en"], { type: "region" });
    }
  }, [i18n.language]);

  const pctFmt = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language, {
        maximumFractionDigits: 2,
      }),
    [i18n.language]
  );

  const nameFor = (code: string) => {
    try {
      return regionNames.of(code) ?? code;
    } catch {
      return code;
    }
  };

  const grouped = useMemo(() => {
    const buckets: Record<Region, DividendTaxRate[]> = {
      europe: [],
      americas: [],
      asiaPacific: [],
      mea: [],
      other: [],
    };
    for (const c of DIVIDEND_TAX_RATES) buckets[c.region].push(c);
    for (const r of REGION_ORDER) {
      buckets[r].sort((a, b) =>
        nameFor(a.code).localeCompare(nameFor(b.code), i18n.language)
      );
    }
    return buckets;
  }, [i18n.language, regionNames]);

  const selectedLabel =
    countryCode === CUSTOM_RATE_CODE
      ? `${t("calculator.form.customRateOption")} · ${pctFmt.format(customTaxRatePct)}%`
      : (() => {
          const c = DIVIDEND_TAX_RATES.find((x) => x.code === countryCode);
          if (!c) return t("calculator.form.country") as string;
          return `${nameFor(c.code)} · ${pctFmt.format(c.rate * 100)}%`;
        })();

  const triggerCls =
    "h-10 w-full px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 flex items-center justify-between gap-2 text-start";

  return (
    <div className="space-y-2 pt-3 mt-2 border-t border-border">
      <div className="flex items-baseline justify-between gap-2">
        <label className="text-sm font-medium text-foreground/90">
          Country (withdrawal tax)
        </label>
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" role="combobox" aria-expanded={open} className={triggerCls}>
            <span className="truncate">{selectedLabel}</span>
            <ChevronsUpDown className="h-4 w-4 opacity-60 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[320px]" align="start">
          <Command
            filter={(value, search) =>
              value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
            }
          >
            <div className="flex items-center border-b px-3">
              <Search className="me-2 h-4 w-4 shrink-0 opacity-50" />
              <CommandInput
                placeholder={t("calculator.form.searchCountry") as string}
                className="h-10 border-0 focus:ring-0"
              />
            </div>
            <CommandList className="max-h-72">
              <CommandEmpty>{t("calculator.form.noResults")}</CommandEmpty>

              <CommandGroup heading={t("calculator.regions.other") as string}>
                <CommandItem
                  value={`${t("calculator.form.customRateOption")} custom`}
                  onSelect={() => {
                    onCountryChange(CUSTOM_RATE_CODE);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "me-2 h-4 w-4",
                      countryCode === CUSTOM_RATE_CODE ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {t("calculator.form.customRateOption")}
                </CommandItem>
              </CommandGroup>

              {REGION_ORDER.filter((r) => grouped[r].length > 0).map((region) => (
                <CommandGroup
                  key={region}
                  heading={t(`calculator.regions.${region}`) as string}
                >
                  {grouped[region].map((c) => {
                    const dn = nameFor(c.code);
                    return (
                      <CommandItem
                        key={c.code}
                        value={`${dn} ${c.code}`}
                        onSelect={() => {
                          onCountryChange(c.code);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "me-2 h-4 w-4",
                            countryCode === c.code ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="flex-1">{dn}</span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {pctFmt.format(c.rate * 100)}%
                          {c.isEstimate ? ` · ${t("calculator.form.estLabel")}` : ""}
                        </span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {countryCode === CUSTOM_RATE_CODE && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground shrink-0">
            {t("calculator.form.customRateLabel")}
          </label>
          <input
            type="number"
            min={0}
            max={60}
            step={0.1}
            className="h-9 flex-1 px-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 tabular-nums"
            value={customTaxRatePct}
            onChange={(e) =>
              onCustomRateChange(
                Math.min(60, Math.max(0, e.target.value === "" ? 0 : Number(e.target.value)))
              )
            }
          />
          <span className="text-xs text-muted-foreground">%</span>
        </div>
      )}

      <p className="text-[11px] leading-snug text-muted-foreground">
        Estimated effective tax on retirement withdrawals. Use “Custom rate” for your own number.
      </p>
    </div>
  );
};

export default CountryTaxPicker;
